import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit";
import type { QueryClient } from "@tanstack/react-query";
import type { UndoableAssignment } from "@/components/Pages/Diary/SpaceScanner/SpaceScanner.types";
import type { BookingUpdateContext } from "@/utils/booking";
import {
  undoOptimisation,
  type UndoOptimisationResult
} from "@/components/Pages/Diary/SpaceScanner/undoOptimisation";
import type { RootState } from "../store";

export type SpaceOptimiserRunStatus = "applied" | "undone" | "dismissed";

export interface SpaceOptimiserRun {
  status: SpaceOptimiserRunStatus;
  assignments: UndoableAssignment[];
  venueId: string;
  dateKey: string;
  undoFailureCount: number;
}

export type SpaceOptimiserState = {
  run: SpaceOptimiserRun | null;
  isUndoing: boolean;
};

export const initialSpaceOptimiserState: SpaceOptimiserState = {
  run: null,
  isUndoing: false
};

type AppliedRun = {
  assignments: UndoableAssignment[];
  venueId: string;
  dateKey: string;
};

type UndoRunArgs = {
  diaryContext: Omit<BookingUpdateContext, "queryClient">;
  queryClient: QueryClient;
  onProgress?: (n: number) => void;
};

type UndoRunPayload = {
  result: UndoOptimisationResult;
  isCurrentRun: boolean;
};

const emptyUndoResult: UndoOptimisationResult = {
  revertedBookingIds: [],
  failures: []
};

export const undoSpaceOptimiserRun = createAsyncThunk<
  UndoRunPayload,
  UndoRunArgs,
  { state: { spaceOptimiser: SpaceOptimiserState } }
>(
  "spaceOptimiser/undoRun",
  async ({ diaryContext, queryClient, onProgress }, { getState }) => {
    const run = getState().spaceOptimiser.run;
    if (!run) {
      return { result: emptyUndoResult, isCurrentRun: false };
    }

    const result = await undoOptimisation({
      assignments: run.assignments,
      diaryContext,
      queryClient,
      onProgress
    });

    return {
      result,
      isCurrentRun: getState().spaceOptimiser.run === run
    };
  },
  {
    condition: (_, { getState }) => {
      const { run, isUndoing } = getState().spaceOptimiser;
      return run?.status === "applied" && !isUndoing;
    }
  }
);

const spaceOptimiserSlice = createSlice({
  name: "spaceOptimiser",
  initialState: initialSpaceOptimiserState,
  reducers: {
    appliedRunRecorded: (state, action: PayloadAction<AppliedRun>) => {
      const { assignments, venueId, dateKey } = action.payload;
      state.run =
        assignments.length === 0
          ? null
          : {
              status: "applied",
              assignments,
              venueId,
              dateKey,
              undoFailureCount: 0
            };
    },
    runDismissed: (state) => {
      if (state.run) {
        state.run.status = "dismissed";
        state.run.assignments = [];
        state.run.undoFailureCount = 0;
      }
    },
    runCleared: (state) => {
      state.run = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(undoSpaceOptimiserRun.pending, (state) => {
        state.isUndoing = true;
      })
      .addCase(undoSpaceOptimiserRun.rejected, (state) => {
        state.isUndoing = false;
      })
      .addCase(undoSpaceOptimiserRun.fulfilled, (state, action) => {
        state.isUndoing = false;
        const { result, isCurrentRun } = action.payload;
        if (!isCurrentRun || !state.run) {
          return;
        }

        if (result.failures.length === 0) {
          state.run.status = "undone";
          state.run.assignments = [];
          state.run.undoFailureCount = 0;
          return;
        }

        state.run.assignments = result.failures.map(
          ({ assignment }) => assignment
        );
        state.run.undoFailureCount = result.failures.length;
      });
  }
});

export const { appliedRunRecorded, runDismissed, runCleared } =
  spaceOptimiserSlice.actions;

export const selectSpaceOptimiserRun = (
  state: RootState
): SpaceOptimiserRun | null => state.spaceOptimiser.run;
export const selectIsSpaceOptimiserUndoing = (state: RootState): boolean =>
  state.spaceOptimiser.isUndoing;

export const spaceOptimiserReducer = spaceOptimiserSlice.reducer;
export default spaceOptimiserReducer;
